<script>
export default {
  props: {
    type: {
      type: String,
      default: 'button',
      validator(value) {
        return ['button', 'submit'].includes(value)
      },
    },
    to: {
      type: [Object, String],
      default: '',
    },
    href: {
      type: String,
      default: '',
    },
    target: {
      type: String,
      default: '_blank',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    replace: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    toUpperCasefirst(str) {
      const strarr = str.split(' ')
      let result = ''

      for (let i in strarr) {
        result = `${strarr[i].substring(0, 1).toUpperCase()}${strarr[i].substring(1)}`
      }

      return result
    },
  },
}
</script>

<template>
  <a v-if="href" :href="href" :target="target" :disabled="disabled" class="baseButton" v-on="$listeners">
    <span class="baseButton__content"><slot /></span>
  </a>
  <router-link v-else-if="to" :to="to" :disabled="disabled" class="baseButton" v-on="$listeners">
    <span class="baseButton__content"><slot /></span>
  </router-link>
  <button v-else :type="type" :disabled="disabled" class="baseButton" :class="{ isLoading: loading }" v-on="$listeners">
    <span class="baseButton__content"><slot /></span>
  </button>
</template>
<style lang="scss">
$colors: (
  'primary': (
    'color': $color-primary,
    'color-hover': darken($color-primary, 5%),
    'text': #fff,
  ),
  'secondary': (
    'color': $color-secondary,
    'color-hover': darken($color-secondary, 5%),
    'text': #fff,
  ),
  'danger': (
    'color': #b31f29,
    'color-hover': darken($color-danger, 5%),
    'text': #fff,
  ),
  'dark': (
    'color': #4d5561,
    'color-hover': darken(#4d5561, 5%),
    'text': #fff,
  ),
  'grayLight': (
    'color': #e8eaee,
    'color-hover': #d1d5dd,
    'text': $color-gray-7,
  ),
  'gray': (
    'color': $color-gray-5,
    'color-hover': $color-gray-5,
    'text': #fff,
  ),
  'white': (
    'color': #fff,
    'color-hover': darken(#fff, 10%),
    'text': $color-primary,
  ),
  'normal': (
    'color': #fff,
    'color-hover': $color-primary,
    'text': $color-gray-7,
    'text-hover': #fff,
    'border': 1px solid $color-gray-3,
  ),
);

.baseButton {
  display: inline-block;
  width: auto;
  max-width: 100%;
  padding: 1.8rem 3.6rem;
  font-size: 1.25rem;
  font-weight: 400;
  line-height: 1.15;
  color: #fff;
  text-align: center;
  letter-spacing: 0.2em;
  white-space: nowrap;
  cursor: pointer;
  user-select: none;
  background: grey;
  border: 1px solid transparent;
  opacity: 1;
  transition: background 0.3s, border 0.3s, color 0.3s, opacity 0.3s, backdrop-filter 0.3s;
  -webkit-appearance: none;
  appearance: none;

  @include max-lg {
    padding: 1rem 3.6rem;
    font-size: 1rem;
  }

  &:focus {
    outline: none;
  }

  &:hover {
    text-decoration: none;
  }

  &:disabled,
  &.isLoading {
    opacity: 0.65;
  }

  &:disabled {
    cursor: not-allowed;
  }

  &.isLoading {
    cursor: wait;
  }

  &.en {
    letter-spacing: 0;
  }

  @each $color, $value in $colors {
    &.isStyle#{capitalize($color)} {
      color: map-get($value, 'text');
      background: map-get($value, 'color');
      border: map-get($value, 'border');

      &:hover:not(:disabled) {
        background: map-get($value, 'color-hover');
        color: map-get($value, 'text-hover');
      }

      &:active:not(:disabled) {
        background: darken(map-get($value, 'color-hover'), 10%);
      }

      &.isOutline {
        color: map-get($value, 'color');
        border: 1px solid map-get($value, 'color');

        &:hover:not(:disabled) {
          background-color: map-get($value, 'color');
          border: 1px solid map-get($value, 'color');
        }

        &:active:not(:disabled) {
          background-color: darken(map-get($value, 'color'), 15%);
        }

        &.isStyleGray {
          color: $color-gray-5;
          border: 1px solid $color-gray-5;
        }
      }

      &.isOutline2 {
        &:hover:not(:disabled) {
          color: map-get($value, 'text');
          background-color: map-get($value, 'color');
          border-color: map-get($value, 'color');
        }

        &:active:not(:disabled) {
          color: darken(map-get($value, 'text'), 10%);
          background-color: darken(map-get($value, 'color'), 10%);
          border-color: darken(map-get($value, 'color'), 10%);
        }
      }

      &.isOutline3 {
        color: map-get($value, 'color');

        &:hover:not(:disabled) {
          background-color: map-get($value, 'color');
          border-color: map-get($value, 'color');
        }

        &:active:not(:disabled) {
          color: darken(map-get($value, 'color'), 10%);
          background-color: darken(map-get($value, 'color'), 10%);
          border-color: darken(map-get($value, 'color'), 10%);
        }
      }

      &.isText {
        &:hover:not(:disabled) {
          color: map-get($value, 'color');
        }

        &:active:not(:disabled) {
          color: darken(map-get($value, 'color'), 10%);
        }

        &.isDanger {
          color: #cd0202;
        }
      }

      &.isTextUnderline {
        color: map-get($value, 'color');

        &:hover:not(:disabled) {
          color: map-get($value, 'color-hover');
        }

        &:active:not(:disabled) {
          color: darken(map-get($value, 'color'), 10%);
        }
      }

      &.isGlass {
        color: map-get($value, 'color');
        border: 1px solid map-get($value, 'color');
        backdrop-filter: blur(8px);

        &:hover:not(:disabled) {
          background: rgba(map-get($value, 'color'), 0.3);
          border: 1px solid map-get($value, 'color');
        }

        &:active:not(:disabled) {
          background: rgba(map-get($value, 'color'), 0.1);
        }

        &.isStyleGray {
          color: $color-gray-5;
          border: 1px solid $color-gray-5;
        }

        &.isStyleWhite {
          &:hover:not(:disabled) {
            background: rgba(darken(map-get($value, 'color'), 80%), 0.3);
          }
        }
      }
    }
  }

  &.isStyleField {
    max-width: none;
    height: 55px;
    padding: 0 1rem;
    margin-right: -22px;
    margin-left: -22px;
    font-size: 1rem;
    color: #1d293f;
    letter-spacing: 0.1rem;
    background: #fff;
    border: 1px solid #b1baca;
    border-radius: 0;

    &:hover:not(:disabled) {
      color: $color-primary;
      border-color: #7484a1;
    }

    &:active:not(:disabled) {
      color: lighten($color-primary, 10%);
    }
    @include max-md {
      font-size: 0.8125rem;
      // padding: 0 0.75em;
    }
    @include max-2sm {
      font-size: 0.75rem;
      padding: 0 0.75em;
    }
  }

  &.isText {
    padding: 0;
    font-size: inherit;
    color: inherit;
    letter-spacing: inherit;
    background: transparent;
    border-color: transparent;

    &:hover:not(:disabled) {
      background: transparent;
      // border-color: transparent !important;
      border-bottom: 1px solid $color-gray-5;
    }

    &:active:not(:disabled) {
      background: transparent;
      // border-color: transparent !important;
      border-bottom: 1px solid $color-gray-5;
    }
  }

  &.isTextUnderline {
    padding: 0;
    font-size: inherit;
    color: inherit;
    text-decoration: underline;
    letter-spacing: inherit;
    background: transparent;
    border-color: transparent;

    &:hover:not(:disabled) {
      background: transparent;
      border-color: transparent !important;
    }

    &:active:not(:disabled) {
      background: transparent;
      border-color: transparent !important;
    }
  }

  &.isRadius {
    border-radius: 5px;
  }

  &.isFillet {
    border-radius: 9527px;
  }

  &.isAuto {
    width: auto;
  }

  &.isFull {
    width: 100%;
  }

  &.isSmaller {
    padding: 0.4rem 0.8rem;
    font-size: 0.875rem;
  }

  &.isSmall {
    padding: 1.1rem 2rem;
    font-size: 1rem;
    @include max-md {
      padding: 0.8rem 1.5rem;
      font-size: 0.875rem;
    }
  }

  &.isBig {
    padding: 3.28rem 4.92rem;
    font-size: 1.25rem;

    @include max-md {
      font-size: 1rem;
    }

    &.isCol1 {
      padding: 3.4rem 0;
    }

    &.isCol2 {
      padding: 3.4rem 3rem;
    }
  }

  @for $i from 1 through 12 {
    &.isCol#{$i} {
      width: #{(($container - ($container-gap * 2)) - (((12 / $i) - 1)) * ($container-gap * 2)) / (12 / $i)};
    }
    &.isCol1 {
      padding-right: 0.5rem;
      padding-left: 0.5rem;
    }
  }

  &.isOutline {
    background: #fff;

    &:hover:not(:disabled) {
      color: #fff;
    }
  }

  &.isOutline2 {
    color: $color-gray-7;
    background: #fff;
    border: 2px solid $color-gray-3;
    display: flex;
    justify-content: center;

    &:hover:not(:disabled) {
      color: #fff;
    }
    // &:active:not(:disabled) {
    //   color: #FFF;
    // }
  }

  &.isOutline3 {
    background: #fff;
    border: 2px solid $color-gray-3;

    &:hover:not(:disabled) {
      color: #fff;
    }
  }

  &.isGlass {
    background: transparent;
  }

  &.isCircle {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 80px;
    height: 80px;
    padding: 0;
    padding-left: 4px;
    border: unset;
    border-radius: 100%;

    &.isSmaller {
      width: 30px;
      height: 30px;
    }

    &.isSmall {
      width: 55px;
      height: 55px;
    }

    &.isBig {
      width: 130px;
      height: 130px;
    }
  }

  &.isLoading {
    position: relative;
    pointer-events: none;

    .baseButton__content {
      opacity: 0;
    }

    &::after {
      position: absolute;
      top: calc(50% - 0.6em);
      left: calc(50% - 0.6em);
      display: block;
      width: 1.2em;
      height: 1.2em;
      content: '';
      border: 2.5px solid;
      border-top-color: transparent;
      border-right-color: transparent;
      border-radius: 290486px;
      animation: spinAround 0.6s infinite linear;
    }
  }

  &.isSizeGetcode {
    width: 200px;
    padding: 1.05rem 1.75rem;
    font-size: 18px;
    @include max-lg {
      padding: 1.02rem 1.75rem;
      position: relative;
      top: -4px;
    }
    @include max-md {
      width: 100%;
      position: inherit;
    }
  }

  &.isPosition {
    position: relative;
    z-index: 3;
  }

  &.isDelete {
    height: 45px;
    padding: 0 15px;
    margin-bottom: 10px;
    font-size: 1rem;
    background: #fff;
    border: 1px solid $color-gray-3;

    &:hover {
      color: $color-gray-7 !important;
      background-color: #fff !important;
    }

    &:not(:last-child) {
      margin-right: 10px;
    }
    @include max-md {
      text-align: -webkit-auto;
      position: relative;
      padding: 0px 32px 0 12px;
      .icon {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        right: 12px;
      }
    }
  }
  &.isEllipsis {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
}

@keyframes spinAround {
  0% {
    transform: rotate(0turn);
  }
  100% {
    transform: rotate(1turn);
  }
}
</style>
